import { type OfferTemplateRes, PurchaseContext, SiteType } from '@on3/api/src';
import { useSubscribe } from '@on3/ui-lib/components/Subscription/Provider';
import { On3PlusRivalsTOC } from '@on3/ui-lib/components/Subscription/Templates/TopOfContent/On3PlusRivals';
import { TheAthleticTOC } from '@on3/ui-lib/components/Subscription/Templates/TopOfContent/TheAthletic';
import { useSite } from '@on3/ui-lib/contexts/SiteProvider';
import { type PurchaseModalProps } from '@on3/ui-lib/types/purchase';
import { createDescription } from '@public/utils/meta';
import clsx from 'clsx';

import styles from './TopOfContent.module.scss';

interface TOCProps {
  handleJoin: (props: PurchaseModalProps) => void;
  props?: Record<string, unknown>;
  className?: string;
  tmpVariant?: string;
  tmpTemplate?: OfferTemplateRes;
}

export type TOCTemplateProps = Omit<TOCProps, 'handleJoin'> & {
  handleJoin: (
    planKey: PurchaseModalProps['planKey'],
    couponKey?: PurchaseModalProps['couponKey'],
  ) => void;
  template: OfferTemplateRes;
  preview?: boolean;
};

const DefaultTOC = ({
  handleJoin,
  template,
  preview = false,
  props,
}: TOCTemplateProps) => {
  const { currentSite } = useSite();
  const { subscribeText } = useSubscribe();

  const isHighSchool =
    currentSite.key === 411 || currentSite.type === SiteType.HighSchool;
  const mascot =
    currentSite.organization?.mascot || isHighSchool ? 'Recruiting' : 'College';
  const title =
    template.data.title ||
    `${mascot} scoop, experts, and community - all in one.`;
  const lineOne = template.data.line1;
  const lineTwo = template.data.line2;
  const lineThree =
    template.data.line3 ||
    createDescription(
      'One subscription: The best {0} coverage and community, and all access to the On3 College Sports Network.',
    );
  const btnText = template.planData1?.buttonText || subscribeText || 'Join Now';
  const planKey = template.planKey1;

  return (
    <div
      className={clsx(styles.block, {
        [styles.preview]: preview,
      })}
      data-template-type="top-of-content"
      {...props}
    >
      <div className={styles.title}>{title}</div>
      <div className={styles.container}>
        {lineOne && <div className={styles.lineOne}>{lineOne}</div>}
        {lineTwo && <div className={styles.lineTwo}>{lineTwo}</div>}
        {lineThree && <div className={styles.lineThree}>{lineThree}</div>}
      </div>
      <button onClick={() => handleJoin(planKey)} type="button">
        {btnText}
      </button>
    </div>
  );
};

export const TOC = ({
  handleJoin,
  className,
  tmpVariant,
  tmpTemplate,
  props,
}: TOCProps) => {
  const {
    isSubscribed,
    templates: { topOfContent: tocTemplate },
  } = useSubscribe();
  const { currentSite } = useSite();

  const isTmpTemplate = Boolean(tmpTemplate);

  const template = tmpTemplate || tocTemplate;
  const variant = tmpVariant || template?.key || 'toc';

  if (!template || (isSubscribed && !isTmpTemplate)) return null;

  const handleTocJoin = (
    planKey: PurchaseModalProps['planKey'],
    couponKey?: PurchaseModalProps['couponKey'],
  ) => {
    handleJoin({ context: PurchaseContext.TopOfContent, planKey, couponKey });
  };

  const tocProps = {
    handleJoin: handleTocJoin,
    template,
    preview: Boolean(tmpVariant),
    props: props || {},
  };

  return (
    <div className={clsx(className, styles.wrapper)}>
      <div
        className={styles.component}
        data-site-template={currentSite.template}
      >
        {variant === 'toc' && <DefaultTOC {...tocProps} />}
        {variant === 'toc-the-athletic' && <TheAthleticTOC {...tocProps} />}
        {variant === 'toc-on3-plus-rivals' && (
          <On3PlusRivalsTOC {...tocProps} />
        )}
      </div>
    </div>
  );
};
